import React, { ReactNode } from "react";
import cn from "classnames";

import styles from "./DetailInfo.module.scss";
import Icon from "../Icon/Icon";

import { CloseIcon, EditGreyIcon } from "Assets";
import { getNestedProperty } from "Shared/Utils/getNestedProperty";

type DetailInfoProps<T extends Record<string, any>> = {
  data: T;
  rows: { label: string; key: keyof T | string; additionalKeys?: string[] }[];
  renderRow?: (
    value: any,
    key: string,
    additionalValues?: Record<string, any>
  ) => ReactNode;
  children?: ReactNode;
  onClose: () => void;
  onClickEdit?: () => void;
  isEditable?: boolean;
  isStatic?: boolean;
  title: string;
  header?: JSX.Element;
  footer?: JSX.Element;
  withoutMaxMinWith?: boolean;
  onClickTitle?: () => void;
};

const DetailInfo = <T extends Record<string, any>>({
  data,
  title,
  renderRow,
  rows,
  children,
  onClose,
  onClickEdit,
  isEditable,
  header,
  footer,
  isStatic,
  withoutMaxMinWith,
  onClickTitle,
}: DetailInfoProps<T>) => {
  return (
    <div
      className={cn(
        styles.detail,
        isStatic && styles.detail_static,
        !withoutMaxMinWith && styles.detail_withMaxMinWith
      )}
    >
      <div className={styles.detail__header}>
        <p onClick={onClickTitle} className={styles.detail__header__title}>
          {title}
        </p>
        <div className={styles.detail__header__icons}>
          {isEditable && (
            <Icon onClick={onClickEdit} component={EditGreyIcon} />
          )}
          {children}
          <Icon onClick={onClose} component={CloseIcon} />
        </div>
      </div>
      <div className={styles.detail__content}>
        {header}
        {rows.map((row, index) => {
          const value = getNestedProperty(data, row.key as string);
          const additionalValues = row.additionalKeys
            ? row.additionalKeys.reduce(
                (acc, key) => {
                  const additionalValue = getNestedProperty(data, key);
                  if (additionalValue !== "---") {
                    acc[key] = additionalValue;
                  }
                  return acc;
                },
                {} as Record<string, any>
              )
            : undefined;

          return (
            <div className={styles.detail__content__item} key={index}>
              <p className={styles.detail__content__item__label}>{row.label}</p>
              <div className={styles.detail__content__item__value}>
                {renderRow
                  ? renderRow(value, row.key as string, additionalValues)
                  : value}
              </div>
            </div>
          );
        })}
        {footer}
      </div>
    </div>
  );
};

export default DetailInfo;
